import {
  getFlightMarkerById,
  getFlightMarkersList,
  ICoords,
  IFlightMarkersState,
  isArchived,
} from "../modules/flightMarkers/flightMarkersReducer";
import {
  IGCPRequestState,
  isSupportedCorruptionStatus,
} from "../modules/gcpRequest/reducer";
import {
  getSiteSrs,
  isSiteImperial,
  ISiteState,
} from "../modules/site/reducer";
import config from "../../config";
import { getNamedGcpRequestFlightMarkers } from "./gcpRequest/namedSuggestedFlightMarkers";
import { areCoordinatesAlmostEqual, feetToMeters } from "../../utils";
import { convertGlobalToLocalCoords } from "../../utils/coordinateConversion/convertGlobalToLocalCoords";
import { convertWgs84ToLocalCoords } from "../../utils/coordinateConversion/convertWgs84ToLocalCoords";
import { convertGlobalToWgs84Coords } from "../../utils/coordinateConversion/convertGlobalToWgs84Coords";
import { ISRS } from "../../apiFacades/csu/site";
import {
  getSuggestedMarkerDescription,
  getCorruptedMarkerDescription,
  getExistingGcpDescription,
  getPlacedMarkerDescription,
  getInstructionText,
} from "./gcpDescriptions";
import { DEFAULT_ELEVATION } from "../../constants/defaultElevation";
import { convertWgs84ToGlobalCoords } from "../../utils/coordinateConversion/convertWgs84ToGlobalCoords";

const suggestedFlightMarkerElevation = config.proposedFlightMarkerAltitude;

export interface IGcpRowItem {
  name: string;
  x: number;
  y: number;
  z: number;
  description: string;
}

export type IExportType = "wgs84" | "site_srs" | "global";

const getExportCoordinatesFromGlobal = (
  globalCoords: ICoords,
  srs: ISRS,
  exportType: IExportType,
): ICoords => {
  switch (exportType) {
    case "wgs84":
      return convertGlobalToWgs84Coords(globalCoords, srs);
    case "site_srs":
      return convertGlobalToLocalCoords(globalCoords, srs);
    case "global":
      return globalCoords;
    default:
      throw Error(`Unknown export type selected '${exportType}'`);
  }
};

const getExportCoordinatesFromWgs84 = (
  wgs84: ICoords,
  srs: ISRS,
  exportType: IExportType,
): ICoords => {
  switch (exportType) {
    case "wgs84":
      return wgs84;
    case "site_srs":
      return convertWgs84ToLocalCoords(wgs84, srs);
    case "global":
      return convertWgs84ToGlobalCoords(wgs84, srs);
    default:
      throw Error(`Unknown export type selected '${exportType}'`);
  }
};

// It should not be needed to convert elevation to meters in wgs84, but okay to leave it as is for now (per Kirill)
const getExportElevation = (
  elevation: number,
  isImperial: boolean,
  exportType: IExportType,
): number => {
  switch (exportType) {
    case "wgs84":
      return isImperial ? feetToMeters(elevation) : elevation;
    case "site_srs":
    case "global":
      return elevation;
    default:
      throw Error(`Unknown export type selected '${exportType}'`);
  }
};

export const getPlacedMarkersRows = (
  flightMarkersState: IFlightMarkersState,
  state: ISiteState,
  exportType: IExportType,
  gcpRequestState?: IGCPRequestState,
): IGcpRowItem[] => {
  const srs = getSiteSrs(state);
  const isImperial = isSiteImperial(state);

  const { corruptedFlightMarkers, instructions = {} } = gcpRequestState || {};

  const placedMarkerList = getFlightMarkersList(flightMarkersState) || [];
  return (
    placedMarkerList
      .filter((flightMarker) => !isArchived(flightMarker))
      // this filter is needed because getCorruptedMarkerRows will duplicate
      // corrupted markers otherwise, but for cases without gcp request we can just avoid supplying it
      .filter((flightMarker) => !corruptedFlightMarkers?.[flightMarker.id])
      .map(({ id, name, comment, operatorCoords }) => {
        const coords = getExportCoordinatesFromGlobal(
          operatorCoords,
          srs,
          exportType,
        );

        const elevation = getExportElevation(
          operatorCoords.z,
          isImperial,
          exportType,
        );

        const descriptionText = getPlacedMarkerDescription(name, comment);
        const instructionsText = getInstructionText(id, instructions);

        return {
          name,
          x: coords.x,
          y: coords.y,
          z: elevation,
          description: [descriptionText, instructionsText]
            .filter((text) => Boolean(text.length))
            .join(" - "),
        };
      })
  );
};

// Not the best idea to just copy paste a method but more
// elegant solution (cascading filters) would require refactoring
export const getRemeasureOnlyRows = (
  flightMarkersState: IFlightMarkersState,
  state: ISiteState,
  exportType: IExportType,
  gcpRequestState?: IGCPRequestState,
): IGcpRowItem[] => {
  const srs = getSiteSrs(state);
  const isImperial = isSiteImperial(state);

  const { corruptedFlightMarkers, instructions = {} } = gcpRequestState || {};

  const placedMarkerList = getFlightMarkersList(flightMarkersState) || [];
  return (
    placedMarkerList
      .filter((flightMarker) => !isArchived(flightMarker))
      // this filter is needed because getCorruptedMarkerRows will duplicate
      // corrupted markers otherwise, but for cases without gcp request we can just avoid supplying it
      .filter((flightMarker) => !corruptedFlightMarkers?.[flightMarker.id])
      // filter out markers that are not remeasured
      .filter((flightMarker) =>
        Boolean((instructions[flightMarker.id] || []).length),
      )
      .map(({ id, name, comment, operatorCoords }) => {
        const coords = getExportCoordinatesFromGlobal(
          operatorCoords,
          srs,
          exportType,
        );

        const elevation = getExportElevation(
          operatorCoords.z,
          isImperial,
          exportType,
        );

        const descriptionText = getPlacedMarkerDescription(name, comment);
        const instructionsText = getInstructionText(id, instructions);

        return {
          name,
          x: coords.x,
          y: coords.y,
          z: elevation,
          // it's unlikely that both of these will be together since description
          // text is relevant only for markers that are actually of existing class
          // (but saved as gcp or qc due to lack of existing points outside gcp request)
          description: [descriptionText, instructionsText]
            .filter((text) => Boolean(text.length))
            .join(" - "),
        };
      })
  );
};

export const getCorruptedMarkerRows = (
  gcpRequestState: IGCPRequestState,
  flightMarkersState: IFlightMarkersState,
  state: ISiteState,
  exportType: IExportType,
): IGcpRowItem[] => {
  const srs = getSiteSrs(state);
  const isImperial = isSiteImperial(state);

  const { corruptedFlightMarkers, corruptedFlightMarkersOrder } =
    gcpRequestState;
  const corruptedFlightMarkersRows = corruptedFlightMarkersOrder
    .filter((flightMarkerId) => {
      const flightMarker = getFlightMarkerById(
        flightMarkersState,
        flightMarkerId,
      );
      const corruptionStatus = corruptedFlightMarkers[flightMarkerId];
      const isSupportedStatus = isSupportedCorruptionStatus(corruptionStatus);
      return flightMarker && isSupportedStatus && !isArchived(flightMarker);
    })
    .map((flightMarkerId) => {
      const { name, operatorCoords } = getFlightMarkerById(
        flightMarkersState,
        flightMarkerId,
      );
      const coords = getExportCoordinatesFromGlobal(
        operatorCoords,
        srs,
        exportType,
      );
      const elevation = getExportElevation(
        operatorCoords.z,
        isImperial,
        exportType,
      );
      const corruptionStatus = corruptedFlightMarkers[flightMarkerId];
      const instructionsText = getInstructionText(
        flightMarkerId,
        gcpRequestState.instructions,
      );
      const corruptionText = getCorruptedMarkerDescription(corruptionStatus);
      return {
        name,
        x: coords.x,
        y: coords.y,
        z: elevation,
        description: [corruptionText, instructionsText]
          .filter((text) => Boolean(text.length))
          .join(" - "),
      };
    });
  return corruptedFlightMarkersRows;
};

export const getSuggestedMarkerRows = (
  gcpRequestState: IGCPRequestState,
  flightMarkersState: IFlightMarkersState,
  siteState: ISiteState,
  exportType: IExportType,
): IGcpRowItem[] => {
  const srs = getSiteSrs(siteState);

  return getNamedGcpRequestFlightMarkers(
    gcpRequestState,
    flightMarkersState,
  ).map(({ name, type, flightMarker }) => {
    const { lat, lng } = flightMarker.coordinates;
    const markerCoords = { x: lng, y: lat, z: suggestedFlightMarkerElevation };
    const coords = getExportCoordinatesFromWgs84(markerCoords, srs, exportType);
    const description = getSuggestedMarkerDescription(type);
    return {
      name,
      x: coords.x,
      y: coords.y,
      z: coords.z,
      description,
    };
  });
};

export const getExistingGcpRows = (
  gcpRequestState: IGCPRequestState,
  siteState: ISiteState,
  exportType: IExportType,
): IGcpRowItem[] => {
  const srs = getSiteSrs(siteState);

  // There are 3 types of existing controls (and one request can contain all 3 of them):
  // 1. Old existing controls where we didn't save original coordinates, so we can't know if there was a shift, - we will treat them as "approximate"
  // 2. Existing controls with a shift, - we will treat them as "approximate"
  // 3. Existing controls without a shift, - we will treat them as "precise"

  const sortedControls = gcpRequestState.existingGcps.sort(
    (a, b) => +b.highPriority - +a.highPriority,
  );

  return sortedControls.map((existingGcp) => {
    // Assumed CRS is a rare edge case here, - it's unlikely we will have existing controls and an already set matrix
    const coordinates = srs?.isAssumed
      ? convertGlobalToLocalCoords(existingGcp.coordinates, srs)
      : existingGcp.coordinates;

    const isApproximate =
      !existingGcp.originalCoordinates ||
      !areCoordinatesAlmostEqual(
        existingGcp.originalCoordinates,
        coordinates,
        1,
        true,
      );

    const name = existingGcp.name + (isApproximate ? "_APPROX" : "");

    const description = getExistingGcpDescription(
      existingGcp.measurementGuideComment,
      isApproximate,
      existingGcp.highPriority,
    );

    const coords = getExportCoordinatesFromGlobal(
      existingGcp.coordinates,
      srs,
      exportType,
    );

    // For Existing Controls we always use the elevation "-99999". This is to make sure pilots do measure the elevation,
    // and not just upload back the elevation they got. GIS can visually validate horizontal coordinates, but
    // there is no way for them to validate the elevation.
    const z = DEFAULT_ELEVATION;

    return {
      name,
      description,
      ...coords,
      z,
    };
  });
};
